<template>
  <div v-if="grades" ref="root" class="mb-32 mt-56 md:mt-72">
    <p class="heading-3 mb-12 md:text-center">
      {{ i18n(translations.caption) }}
    </p>

    <div class="space-y-24 md:space-y-32">
      <h2 class="punchline-sans md:text-center">
        {{ i18n(translations.title) }}
      </h2>
      <p class="body-1 md:text-center max-w-[760px] md:mx-auto">
        {{ i18n(translations.paragraph) }}
      </p>

      <ProductConditionSwitcher v-if="hasScreenAndBody" v-model="switcher" />
      <ProductConditionBody
        v-if="switcher === 'body'"
        :brand
        :grades
        :is-premium-definition-updated
      />
      <ProductConditionScreen v-if="switcher === 'screen'" :brand :grades />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onUnmounted, ref, useTemplateRef } from 'vue'

import type { GetPickersResponse } from '@backmarket/http-api/src/api-specs-navigation-experience/product/pickers'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useTracking } from '@backmarket/nuxt-module-tracking/useTracking'
import { useIntersectionObserver } from '@vueuse/core'

import translations from './ProductConditionBlock.translations'
import ProductConditionBody from './components/ProductConditionBody/ProductConditionBody.vue'
import type { SwitcherValue } from './components/ProductConditionCard/components/ProductConditionSwitcher/ProductConditionSwitcher.types'
import ProductConditionSwitcher from './components/ProductConditionCard/components/ProductConditionSwitcher/ProductConditionSwitcher.vue'
import ProductConditionScreen from './components/ProductConditionScreen/ProductConditionScreen.vue'

const i18n = useI18n()
const root = useTemplateRef<HTMLElement | null>('root')
const { trackProductBlockImpression } = useTracking()

const { brand, isPremiumDefinitionUpdated, pickersResponse } = defineProps<{
  brand: string
  isPremiumDefinitionUpdated?: boolean
  pickersResponse: GetPickersResponse
}>()

const grades = computed(() =>
  pickersResponse.pickerGroups.find((picker) => picker.id === 'grades'),
)

const hasScreenAndBody = computed(
  () => (grades.value?.sideContentV2?.content?.[0]?.items?.length ?? 0) > 1,
)

const switcher = ref<SwitcherValue>('body')

const { stop } = useIntersectionObserver(root, ([{ isIntersecting }]) => {
  if (isIntersecting) {
    trackProductBlockImpression({
      block_name: 'condition_block',
    })
    stop()
  }
})
onUnmounted(() => {
  stop()
})
</script>
